import { createAsyncThunk } from '@reduxjs/toolkit';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { AppDispatch, RootState } from '@store/types';
import { messageHandlerRegistry } from '@reducers/v2/messagesBus/handlerRegistry';
import { removeMessageById } from '@reducers/v2/messagesBus/messagesBus.slice';
import { TTL_FOR_IMAGE } from '@v2/components/imageHandling/constants';
import moment from 'moment';
import { ImageDocumentMessageType, Message } from '@reducers/v2/messagesBus/types';

interface ProcessImageDocumentMessagesPayload {
  imageDocument: ImageDocumentStoreItem;
  callback?: () => void;
}

export const isMessageAliveAndFresh = ({ message, imageDocumentModifiedOn }: { message: Message; imageDocumentModifiedOn: string }) => {
  const now = Date.now();
  const isMessageAlive = message.ttl && now <= message.ttl;
  const updatedOnFromMessage = moment(message.ttl - TTL_FOR_IMAGE).utc();
  const updatedOnFromImage = moment(imageDocumentModifiedOn);
  const isEventFresh = updatedOnFromMessage.isAfter(updatedOnFromImage);

  return { isMessageAlive, isEventFresh };
};

export const processImageDocumentMessages = createAsyncThunk<void, ProcessImageDocumentMessagesPayload, { dispatch: AppDispatch; state: RootState }>(
  'imageHandlingV2/processImageDocumentMessages',
  async ({ imageDocument }, { dispatch, getState }) => {
    const state = getState();
    const messages = state.messagesBus?.messages?.[imageDocument.documentId!] || [];

    for (const message of messages) {
      // Check if messageType is in ImageDocumentMessageType enum
      if (Object.values(ImageDocumentMessageType).includes(message.messageType as ImageDocumentMessageType)) {
        // Check if message is still alive using TTL
        const { isEventFresh, isMessageAlive } = isMessageAliveAndFresh({ imageDocumentModifiedOn: imageDocument.modifiedOn!, message });

        if (!isMessageAlive || !isEventFresh) {
          // Message has expired, remove it
          dispatch(removeMessageById({ key: imageDocument.documentId!, messageId: message.id }));
          continue;
        }

        // Process the message since it's already for this documentId
        for (const handler of messageHandlerRegistry.getAll(message.messageType)) {
          dispatch(handler({ key: imageDocument.documentId!, message }));
        }
      }
    }
  }
);
